import 'mdn-polyfills/Array.prototype.includes';
import 'mdn-polyfills/CustomEvent';
import '../polyfills/Function.name';
import '../polyfills/Object.LookupGetter';
import '../polyfills/Object.LookupSetter';

import { FallcoSettings } from '../libs/fallco_settings';
import { StringExtender } from '../libs/utilities/src/StringExtender';

/**
 * Classe da estendere che aggiunge una serie di metodi alla classe che la estende,
 * come ad esempio **trigger_custom_event()** e **expose_to_public()**
 */
class FallcoClass {
  constructor(extendingLibrary) {
    this._lib_name = extendingLibrary; // || StringExtender.toSnakeCase(this.constructor.name)
    
    return this;
  }
  
  /**
   *
   * @param name
   * @private
   */
  _create_fake_class(name, _class) {
    let fake_class = {};
    
    fake_class[name] = (new Function('return function ' + name + '() {}'))();
    fake_class       = new fake_class[name]();
    
    return fake_class;
  }
  
  /**
   *
   * @param {Class|*} _class
   */
  _return_public_methods(_class) {
    let to_return     = {};
    let keys_to_avoid = ['constructor'];
    let class_name    = _class.constructor.name;
    let new_class     = this._create_fake_class(class_name, _class);
    /**
     * Espressione che controlla se la stringa NON inizia con _
     * Questa indica una stringa "PUBBLICA"
     * @type {RegExp}
     */
    let public_match  = new RegExp(/^(?!_)/);
    /**
     * Espressione che controlla se la stringa INIZIA con _ seguito da un carattere alfabetico
     * Questa indica una stringa "PRIVATA"
     * @type {RegExp}
     */
    let private_match = new RegExp(/^(_[a-zA-Z])/);
    
    let variables = Object.getOwnPropertyNames(_class);
    let methods   = Object.getOwnPropertyNames(_class.constructor.prototype);
    
    if (!this._lib_name) {
      this._lib_name = StringExtender.toSnakeCase(class_name);
    }
    
    variables.map((key, content, array) => {
      /**
       * Controlla se la key è contenuta nelle "kyes_to_avoid".
       * Se si, salta l'elemento
       */
      if (keys_to_avoid.includes(key)) {
        return;
      }
      
      // Se la key è di tipo pubblico, la aggiunge all'object di ritorno.
      if (key.match(public_match)) {
        new_class[key] = _class[key];
      }
    });
    
    methods.map((key, content, array) => {
      /**
       * Controlla se la key è contenuta nelle "kyes_to_avoid".
       * Se si, salta l'elemento
       */
      if (keys_to_avoid.includes(key)) {
        return;
      }
      
      // Cerco di prendere i getters o i setters
      if (_class.__lookupGetter__ && (_class.__lookupGetter__(key) || _class.__lookupSetter__(key))) {
        let pattern = {};
        
        if (_class.__lookupGetter__(key)) {
          
          Object.assign(pattern, {
            get() {
              return _class[key];
            }
          });
        }
        
        if (_class.__lookupSetter__(key)) {
          Object.assign(pattern, {
            set(val) {
              _class[key] = val;
            }
          });
        }
        
        Object.defineProperty(new_class, key, pattern);
      } else if (key.match(public_match)) {
        // Se la key è di tipo pubblico, la aggiunge all'object di ritorno.
        
        // Si deve usare una ArrowFunction in modo che il this posso continuare a funzionare
        new_class.constructor.prototype[key] = (...args) => _class[key](...args);
      }
    });
    
    //
    // new_class = new_class.constructor.bind(_class);
    
    return new_class;
  }
  
  /**
   * Data una classe, permette di esporre solo i metodi e le proprietà pubbliche,
   * nascondendo quindi quelle private, cioè quell che iniziano con "_".
   *
   * @example
   * constructor() {
   *   super();
   *
   *   ...
   *
   *   return super.expose_to_public("<libname>", this)
   * }
   *
   * @param {string} name
   * @param {ClassDecorator | *} content
   * @return {ClassDecorator}
   */
  expose_to_public(name, content) {
    let fallco_settings = new FallcoSettings();
    
    return fallco_settings._add_component(name, this._return_public_methods(content));
  }
  
  /**
   * @summary
   * Metodo da usare nell classi che estendono FallcoClass, il quale triggera un evento personalizzato sul target specificato.
   *
   * @description
   * Al nome dell'evento verrà appeso il contenuto di *settings.suffix*,
   * quindi l'evento risultante sarà dunque composto da **<settings.name><settings.suffix>**
   *
   * @example
   * // nome libreria = "form_validator"
   *
   * super.trigger_custom_event({
   *       target         : this.control,
   *       name           : "validated",
   *       data           : {
   *         dato_personale: "contenuto dato"
   *       },
   *       event_init_data: {bubbles: true},
   *     })
   *
   * this.control.addEventLister("validated.fw.form_validator", e => {
   *   // e.dato_personale -> "contenuto dato"
   *   // e.target -> elemento passato nelle *settings.target*
   * })
   *
   * @param {Object} settings
   * @param {!HTMLElement} settings.target - Questo element sarà visibile nell'evento una volta triggerato sotto **e.target**.
   * @param {!string} settings.name - Nome dell'evento al quale verrà appeso il valore di **settings.suffix**.
   * @param {Object=} settings.data - Dati che verranno aggiunte tra le chiavi dell'evento una volta triggerato.
   * @param {CustomEventInit=} settings.event_init_data - Impostazioni che verranno usate per creare l'evento.
   * @param {Boolean=} settings.event_init_data.bubbles=false
   * @param {Boolean=} settings.event_init_data.cancelable=false
   * @param {Boolean=} settings.event_init_data.composed=false
   * @param {string=} settings.suffix=fw.<lib_name_to_snake_case>
   */
  trigger_custom_event(settings) {
    let custom_event;
    let custom_event_settings;
    let defaultSettings = {
      target:          null,
      name:            '',
      suffix:          'fw.' + this._lib_name,
      data:            {},
      event_init_data: {},
      event_instance:  null
    };
    
    custom_event_settings = Object.assign({}, defaultSettings, settings);
    custom_event          = new (custom_event_settings.event_instance || CustomEvent)(custom_event_settings.name + '.' + custom_event_settings.suffix, custom_event_settings.event_init_data);
    
    Object.assign(custom_event, custom_event_settings.data);
    
    if (!custom_event_settings.target) {
      return console.error('Non \u00E8 stato specificato nessun target per il dispatch dell\'evento', custom_event);
    }
    
    // console.log("Trigger Event:", custom_event_settings);
    
    custom_event_settings.target.dispatchEvent(custom_event);
  }
  
}

export { FallcoClass };
